import { AxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CreateTokenModal } from './components/CreateTokenModal';
import { RemoveTokenModal } from './components/RemoveConfirmationModal';
import { TokenCreatedModal } from './components/TokenCreatedModal';
import { TokenItem } from './components/TokenItem';

import { Button } from '@/components/ui/Button';
import { useCreateAppToken } from '@/hooks/useCreateAppToken';
import { useGetAppTokens } from '@/hooks/useGetAppTokens';
import {
  CreateAppTokenResponseDto,
  AppToken,
} from '@/services/AppTokensService';

export function AppTokens() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.AppTokens',
  });
  const { appTokens } = useGetAppTokens();
  const { createAppToken } = useCreateAppToken();

  const [newTokenName, setNewTokenName] = useState('');
  const [generatedToken, setGeneratedToken] =
    useState<CreateAppTokenResponseDto>();
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<AppToken | null>(null);
  const [isTokenCreatedModalOpen, setIsTokenCreatedModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  async function handleCreateToken() {
    try {
      const generateToken = await createAppToken({ name: newTokenName });

      setGeneratedToken(generateToken);
      setIsCreateModalOpen(false);
      setIsTokenCreatedModalOpen(true);
      setNewTokenName('');
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const { status, data } = error.response;

        if (status === 422 && data?.errors.name) {
          toast.error(data?.errors.name);
          return;
        }

        if (status === 403 && data?.message) {
          toast.error(data?.message);
          return;
        }
      }

      toast.error(t('toast.createError'));
    }
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(generatedToken?.token || '');
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between p-6">
          <div>
            <h2 className="text-lg font-semibold">{t('title')}</h2>

            <p className="text-sm text-muted-foreground">{t('description')}</p>
          </div>

          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createButton')}
          </Button>
        </div>

        {appTokens.length === 0 ? (
          <div className="border-t p-6 text-center text-muted-foreground">
            {t('noTokens')}
          </div>
        ) : (
          <div className="border-t">
            {appTokens.map((token) => (
              <TokenItem
                key={token.id}
                token={token}
                onDelete={() => {
                  setTokenToDelete(token);
                  setIsDeleteModalOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <CreateTokenModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        tokenName={newTokenName}
        onChangeTokenName={setNewTokenName}
        onCreate={handleCreateToken}
      />

      <TokenCreatedModal
        open={isTokenCreatedModalOpen}
        onOpenChange={setIsTokenCreatedModalOpen}
        token={generatedToken || { token: '' }}
        onCopy={copyToClipboard}
        copied={tokenCopied}
      />

      <RemoveTokenModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        token={tokenToDelete}
      />
    </div>
  );
}
